import { Loader, Modal } from '@mantine/core';
import Select from '@xver/components/Atoms/Select/Select';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Action, Direction, NetworkInterface, Protocol } from '../../../../../@types/firewall-rule';
import { FirewallVMRuleSingleObject } from '../../../../../@types/firewall-vm-rule';
import { Button, InputField } from '../../../../../components/Atoms';
import { useCreateFirewallRuleMutate } from '../../../../../hooks/CloudComputeHooks/useCreateFirewallRuleMutate';
import { useUpdateFirewallRuleMutate } from '../../../../../hooks/CloudComputeHooks/useUpdateFirewallRuleMutate';

interface FirewallRuleModalProps {
    opened: boolean;
    onClose: () => void;
    instanceId: number;
    rule?: FirewallVMRuleSingleObject;
}

export default function FirewallRuleModal({
    opened,
    onClose,
    instanceId,
    rule,
}: FirewallRuleModalProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const createMutation = useCreateFirewallRuleMutate();
    const updateMutation = useUpdateFirewallRuleMutate();
    const isEdit = !!rule;

    const DIRECTION_OPTIONS = useMemo(
        () => [
            { value: 'in', label: t('firewall.direction.inbound') },
            { value: 'out', label: t('firewall.direction.outbound') },
        ],
        [t]
    );

    const ACTION_OPTIONS = useMemo(
        () => [
            { value: 'accept', label: t('firewall.action.accept') },
            { value: 'drop', label: t('firewall.action.drop') },
            { value: 'reject', label: t('firewall.action.reject') },
        ],
        [t]
    );

    const PROTOCOL_OPTIONS = useMemo(
        () => [
            { value: '', label: t('firewall.protocol.any') },
            { value: 'tcp', label: t('firewall.protocol.tcp') },
            { value: 'udp', label: t('firewall.protocol.udp') },
            { value: 'icmp', label: t('firewall.protocol.icmp') },
        ],
        [t]
    );

    const INTERFACE_OPTIONS = useMemo(
        () => [
            { value: 'net0', label: t('firewall.interfaceOption.public') },
            { value: 'net1', label: t('firewall.interfaceOption.private') },
        ],
        [t]
    );

    const [direction, setDirection] = useState<Direction>('in');
    const [action, setAction] = useState<Action>('accept');
    const [protocol, setProtocol] = useState<string>('');
    const [iface, setIface] = useState<NetworkInterface>('net0');
    const [port, setPort] = useState('');
    const [source, setSource] = useState('0.0.0.0/0');
    const [destination, setDestination] = useState('');
    const [description, setDescription] = useState('');

    useEffect(() => {
        if (!opened) return;
        if (rule) {
            setDirection(rule.direction);
            setAction(rule.action);
            setProtocol(rule.protocol ?? '');
            setIface(rule.iface ?? 'net0');
            setPort(rule.destination_port ?? rule.source_port ?? '');
            setSource(rule.source ?? '');
            setDestination(rule.destination ?? '');
            setDescription(rule.description ?? '');
        } else {
            setDirection('in');
            setAction('accept');
            setProtocol('');
            setIface('net0');
            setPort('');
            setSource('0.0.0.0/0');
            setDestination('');
            setDescription('');
        }
    }, [opened, rule]);

    const isSaving = createMutation.isPending || updateMutation.isPending;

    const handleSave = () => {
        const body = {
            direction,
            action,
            protocol: (protocol || undefined) as Protocol | undefined,
            iface,
            source: source || undefined,
            destination: destination || undefined,
            destination_port: port || undefined,
            description: description || undefined,
        };
        if (isEdit && rule) {
            updateMutation.mutate(
                { id: instanceId, body: { ...body, position: rule.position } },
                { onSuccess: () => onClose() }
            );
        } else {
            createMutation.mutate({ id: instanceId, body }, { onSuccess: () => onClose() });
        }
    };

    return (
        <Modal
            size="md"
            opened={opened}
            onClose={onClose}
            withCloseButton={false}
            overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
            }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            centered
        >
            <div className="flex flex-col justify-start items-start gap-4">
                <div className="flex flex-col justify-start items-start">
                    <Modal.Title>
                        <span className="text-base font-bold">
                            {isEdit ? t('firewall.editRule') : t('firewall.addRuleTitle')}
                        </span>
                    </Modal.Title>
                    <p className="text-sm text-fg/68">
                        {isEdit
                            ? t('firewall.editRuleDescription')
                            : t('firewall.addRuleDescription')}
                    </p>
                </div>
                <div className="w-full flex flex-col gap-4">
                    <div className="grid grid-cols-2 gap-3">
                        <Select
                            label={t('firewall.fields.direction')}
                            data={DIRECTION_OPTIONS}
                            value={direction}
                            onChange={(v) => v && setDirection(v as Direction)}
                            disabled={isSaving}
                            allowDeselect={false}
                        />
                        <Select
                            label={t('firewall.fields.action')}
                            data={ACTION_OPTIONS}
                            value={action}
                            onChange={(v) => v && setAction(v as Action)}
                            disabled={isSaving}
                            allowDeselect={false}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Select
                            label={t('firewall.fields.protocol')}
                            data={PROTOCOL_OPTIONS}
                            value={protocol}
                            onChange={(v) => setProtocol(v ?? '')}
                            disabled={isSaving}
                            allowDeselect={false}
                        />
                        <InputField
                            label={t('firewall.fields.port')}
                            placeholder={t('firewall.fields.portPlaceholder')}
                            value={port}
                            onChange={(e) => setPort(e.target.value)}
                            disabled={isSaving}
                        />
                    </div>
                    <Select
                        label={t('firewall.fields.interface')}
                        data={INTERFACE_OPTIONS}
                        value={iface}
                        onChange={(v) => v && setIface(v as NetworkInterface)}
                        disabled={isSaving}
                        allowDeselect={false}
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <InputField
                            label={t('firewall.fields.source')}
                            placeholder={t('firewall.fields.sourcePlaceholder')}
                            value={source}
                            onChange={(e) => setSource(e.target.value)}
                            disabled={isSaving}
                        />
                        <InputField
                            label={t('firewall.fields.destination')}
                            placeholder={t('firewall.fields.destinationPlaceholder')}
                            value={destination}
                            onChange={(e) => setDestination(e.target.value)}
                            disabled={isSaving}
                        />
                    </div>
                    <InputField
                        label={t('firewall.fields.description')}
                        placeholder={t('firewall.fields.descriptionPlaceholder')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={isSaving}
                    />
                    <div className="w-full flex flex-col justify-start items-start gap-2.5">
                        <Button width="100%" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? (
                                <div className="flex items-center gap-2">
                                    <Loader size={16} color="white" />
                                    <span>{t('firewall.saving')}</span>
                                </div>
                            ) : isEdit ? (
                                t('firewall.saveChanges')
                            ) : (
                                t('firewall.saveRule')
                            )}
                        </Button>
                        <Button
                            width="100%"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            {t('firewall.cancel')}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
